import { ref, readonly } from 'vue'

const needRefresh = ref(false)
const offlineReady = ref(false)
const updating = ref(false)

let updateSW = null
let initialized = false

/**
 * Register the production service worker once and wire update detection.
 * Call from main.js so checks start as early as possible.
 */
export function initPwaUpdate() {
  if (initialized) return
  initialized = true

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return

  import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const updateIntervalMs = 5 * 60 * 1000
      const buildVersionKey = 'atttrack_pwa_build_version'
      const versionUrl = `${import.meta.env.BASE_URL}version.json`

      const checkForUpdate = async (registration) => {
        if (!registration || !navigator.onLine) return

        try {
          // version.json is network-only in Workbox. A changed build time
          // means a deployment happened even when the app shell is cached.
          const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
            cache: 'no-store',
          })
          if (!response.ok) return

          const version = await response.json()
          const buildId = `${version.version || 'unknown'}:${version.buildTime || ''}`
          const previousBuildId = localStorage.getItem(buildVersionKey)
          localStorage.setItem(buildVersionKey, buildId)

          // Safe when unchanged. When it differs, the waiting SW is installed
          // and onNeedRefresh shows the Update Available UI.
          if (!previousBuildId || previousBuildId !== buildId) {
            await registration.update()
          }
        } catch (err) {
          console.warn('PWA deployment update check failed:', err)
        }
      }

      updateSW = registerSW({
        immediate: true,
        onNeedRefresh() {
          needRefresh.value = true
        },
        onOfflineReady() {
          offlineReady.value = true
          console.log('App ready to work offline')
        },
        onRegisteredSW(_swScriptUrl, registration) {
          if (!registration) return

          const refresh = () => checkForUpdate(registration)
          refresh()
          setInterval(refresh, updateIntervalMs)
          window.addEventListener('focus', refresh)
          window.addEventListener('online', refresh)
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') refresh()
          })
        },
        onRegisterError(error) {
          console.error('Service Worker registration error:', error)
        },
      })
    })
    .catch(() => {
      // virtual:pwa-register unavailable (e.g. unexpected build config)
    })
}

export function usePwaUpdate() {
  const applyUpdate = async () => {
    if (!updateSW || updating.value) return

    updating.value = true
    try {
      // Activates the waiting service worker and reloads all controlled clients
      await updateSW(true)
    } catch (err) {
      console.error('Failed to apply PWA update:', err)
      updating.value = false
    }
  }

  const dismissUpdate = () => {
    needRefresh.value = false
  }

  return {
    needRefresh: readonly(needRefresh),
    offlineReady: readonly(offlineReady),
    updating: readonly(updating),
    applyUpdate,
    dismissUpdate,
  }
}
